import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Pencil, Trash2, Upload, Download, LayoutGrid, RefreshCw, Plus, X } from "lucide-react";
import { pagesForRole } from "@/lib/appPages";
import { TotemMusicPanel } from "@/components/totem/TotemMusicPanel";

const emptyForm = {
  email: "", password: "", name: "", role: "tecnico",
  vehicle: "", equipment_str: "", phone: "", discount_percent: 0, monthly_goal: 0,
  show_lavorazioni: true, home_address: "",
  disable_chiusura: false, disable_camera_scan: false,
  hidden_pages: [],
  subappalto_id: "", discount_percent_2: 0,
  // Cedolino
  cedolino_base: 0,
  bonus_method: "nessuno",
  cedolino_soglia: 8000,
  cedolino_step_produzione: 1000,
  cedolino_step_bonus: 100,
  cedolino_bonus_percent: 0,
  // Totem
  totem_tech_ids: [],
  totem_group_target: 0,
};

export default function TecniciPage() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [openImport, setOpenImport] = useState(false);
  const [impFile, setImpFile] = useState(null);

  // --- Materiale Ricorrente Mensile ---
  const nowYm = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };
  const emptyRecurring = { material_name: "", material_id: null, quantity: 1, unit_price: 0, day_of_month: 1, start_month: nowYm(), end_month: "", notes: "", active: true };
  const [recurringList, setRecurringList] = useState([]);
  const [recForm, setRecForm] = useState(emptyRecurring);
  const [recEditingId, setRecEditingId] = useState(null);
  const [recBusy, setRecBusy] = useState(false);

  const loadRecurring = async (techId) => {
    if (!techId) { setRecurringList([]); return; }
    try {
      const { data } = await api.get("/recurring-supplies", { params: { technician_id: techId } });
      setRecurringList(data || []);
    } catch { setRecurringList([]); }
  };

  const submitRecurring = async () => {
    if (!editingId) return;
    if (!recForm.material_name.trim()) return toast.error("Descrizione obbligatoria");
    if (!recForm.start_month) return toast.error("Mese di inizio obbligatorio");
    setRecBusy(true);
    try {
      const payload = {
        technician_id: editingId,
        material_name: recForm.material_name.trim(),
        material_id: recForm.material_id || null,
        quantity: Number(recForm.quantity) || 0,
        unit_price: Number(recForm.unit_price) || 0,
        day_of_month: Number(recForm.day_of_month) || 1,
        start_month: recForm.start_month,
        end_month: recForm.end_month || null,
        notes: recForm.notes || "",
        active: recForm.active !== false,
      };
      if (recEditingId) {
        await api.patch(`/recurring-supplies/${recEditingId}`, payload);
        toast.success("Aggiornato");
      } else {
        await api.post("/recurring-supplies", payload);
        toast.success("Aggiunto — materializzato per i mesi passati fino a oggi");
      }
      setRecForm(emptyRecurring);
      setRecEditingId(null);
      await loadRecurring(editingId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally {
      setRecBusy(false);
    }
  };

  const editRecurring = (r) => {
    setRecEditingId(r.id);
    setRecForm({
      material_name: r.material_name || "",
      material_id: r.material_id || null,
      quantity: r.quantity || 1,
      unit_price: r.unit_price || 0,
      day_of_month: r.day_of_month || 1,
      start_month: r.start_month || nowYm(),
      end_month: r.end_month || "",
      notes: r.notes || "",
      active: r.active !== false,
    });
  };

  const deleteRecurring = async (r) => {
    const alsoGenerated = window.confirm(
      "Elimino il template.\n\nOK = cancella anche i record già generati (magazzino viaggiante)\nAnnulla = mantieni i record storici, ferma solo le prossime generazioni"
    );
    try {
      await api.delete(`/recurring-supplies/${r.id}?delete_generated=${alsoGenerated}`);
      toast.success("Rimosso");
      await loadRecurring(editingId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const forceGenerate = async () => {
    try {
      const { data } = await api.post("/recurring-supplies/generate");
      toast.success(`Generati ${data.created || 0} nuovi record`);
      await loadRecurring(editingId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  // Materiali del catalogo (per il selettore nel form ricorrenti)
  const [materials, setMaterials] = useState([]);
  useEffect(() => {
    api.get("/materials").then((r) => setMaterials(r.data || [])).catch(() => setMaterials([]));
  }, []);

  const openNew = () => { setForm(emptyForm); setEditingId(null); setRecurringList([]); setRecForm(emptyRecurring); setRecEditingId(null); setOpen(true); };
  const openEdit = (u) => {
    setEditingId(u.id);
    setRecForm(emptyRecurring);
    setRecEditingId(null);
    loadRecurring(u.id);
    setForm({
      email: u.email, password: "", name: u.name, role: u.role,
      vehicle: u.vehicle || "", equipment_str: (u.equipment || []).join(", "),
      phone: u.phone || "", discount_percent: u.discount_percent || 0,
      monthly_goal: u.monthly_goal || 0,
      show_lavorazioni: u.show_lavorazioni !== false,
      home_address: u.home_address || "",
      disable_chiusura: !!u.disable_chiusura,
      disable_camera_scan: !!u.disable_camera_scan,
      hidden_pages: Array.isArray(u.hidden_pages) ? u.hidden_pages : [],
      subappalto_id: u.subappalto_id || "",
      discount_percent_2: u.discount_percent_2 || 0,
      cedolino_base: u.cedolino_base || 0,
      bonus_method: u.bonus_method || "nessuno",
      cedolino_soglia: u.cedolino_soglia ?? 8000,
      cedolino_step_produzione: u.cedolino_step_produzione ?? 1000,
      cedolino_step_bonus: u.cedolino_step_bonus ?? 100,
      cedolino_bonus_percent: u.cedolino_bonus_percent || 0,
      totem_tech_ids: Array.isArray(u.totem_tech_ids) ? u.totem_tech_ids : [],
      totem_group_target: u.totem_group_target || 0,
    });
    setOpen(true);
  };

  const doImport = async () => {
    if (!impFile) return toast.error(t("auto.seleziona_il_file_excel"));
    const form = new FormData();
    form.append("file", impFile);
    try {
      const { data } = await api.post("/users/upload-excel", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.count_created} tecnici creati, ${data.count_skipped} già esistenti, ${data.count_errors} errori`);
      setOpenImport(false);
      setImpFile(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/downloads/tecnici-template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template_tecnici.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore download"); }
  };

  const save = async () => {
    const payload = {
      ...form,
      equipment: form.equipment_str.split(",").map((s) => s.trim()).filter(Boolean),
      discount_percent: Number(form.discount_percent) || 0,
      monthly_goal: Number(form.monthly_goal) || 0,
      show_lavorazioni: !!form.show_lavorazioni,
      home_address: (form.home_address || "").trim() || null,
      disable_chiusura: !!form.disable_chiusura,
      disable_camera_scan: !!form.disable_camera_scan,
      hidden_pages: Array.isArray(form.hidden_pages) ? form.hidden_pages : [],
      subappalto_id: form.subappalto_id || "",
      discount_percent_2: Number(form.discount_percent_2) || 0,
      cedolino_base: Number(form.cedolino_base) || 0,
      bonus_method: form.bonus_method || "nessuno",
      cedolino_soglia: Number(form.cedolino_soglia) || 8000,
      cedolino_step_produzione: Number(form.cedolino_step_produzione) || 1000,
      cedolino_step_bonus: Number(form.cedolino_step_bonus) || 100,
      cedolino_bonus_percent: Number(form.cedolino_bonus_percent) || 0,
      totem_tech_ids: Array.isArray(form.totem_tech_ids) ? form.totem_tech_ids.filter(Boolean) : [],
      totem_group_target: Number(form.totem_group_target) || 0,
    };
    delete payload.equipment_str;
    try {
      if (editingId) {
        const { password, ...rest } = payload;
        const upd = { ...rest };
        if (password) upd.password = password;
        await api.patch(`/users/${editingId}`, upd);
        toast.success(t("common.saved"));
      } else {
        if (!payload.password) return toast.error("Password obbligatoria");
        await api.post("/users", payload);
        toast.success("Creato");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare l'utente?")) return;
    try { await api.delete(`/users/${id}`); toast.success(t("common.deleted")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.team")}</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("menu.users")}</h1>
        </div>
        <Button onClick={openNew} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="btn-new-user">
          <UserPlus className="mr-2 h-4 w-4" /> {t("auto.nuovo_utente")}
        </Button>
        <Button variant="outline" onClick={() => setOpenImport(true)} className="rounded-sm" data-testid="btn-import-tecnici">
          <Upload className="mr-2 h-4 w-4" /> {t("auto.import_excel")}
        </Button>
        <Button variant="ghost" onClick={downloadTemplate} className="rounded-sm text-xs" data-testid="btn-template-tecnici">
          <Download className="mr-1 h-3 w-3" /> {t("auto.template")}
        </Button>
      </div>

      <div className="overflow-x-auto rounded-sm border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
            <tr>
              <th className="p-3">{t("common.name")}</th>
              <th className="p-3">{t("common.email")}</th>
              <th className="p-3">{t("common.role")}</th>
              <th className="p-3">{t("auto.mezzo")}</th>
              <th className="p-3">{t("auto.attrezzatura")}</th>
              <th className="p-3">{t("auto.sconto")}</th>
              <th className="p-3">Obiettivo mese</th>
              <th className="p-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="p-3 font-medium">{u.name}</td>
                <td className="p-3 font-mono text-xs">{u.email}</td>
                <td className="p-3"><Badge variant="outline" className="rounded-sm uppercase">{t("auto." + (u.role || ""), u.role)}</Badge></td>
                <td className="p-3 font-mono text-xs">{u.vehicle || "—"}</td>
                <td className="p-3 text-xs">{(u.equipment || []).join(", ") || "—"}</td>
                <td className="p-3 font-mono">{u.discount_percent || 0}%</td>
                <td className="p-3 font-mono">{u.monthly_goal ? `€ ${Number(u.monthly_goal).toLocaleString("it-IT")}` : <span className="text-slate-400">—</span>}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(u.id)} data-testid={`del-${u.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Modifica utente" : "Nuovo utente"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>{t("common.name")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="form-name" /></div>
            <div className="col-span-2"><Label>{t("common.email")}</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm" data-testid="form-email" /></div>
            <div className="col-span-2"><Label>Password {editingId && "(lascia vuoto per non cambiare)"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-sm" data-testid="form-password" /></div>
            <div>
              <Label>{t("common.role")}</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-sm" data-testid="form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="magazziniere">Magazziniere</SelectItem>
                  <SelectItem value="assistente">Assistente</SelectItem>
                  <SelectItem value="tecnico">{t("auto.tecnico")}</SelectItem>
                  <SelectItem value="subappalto">Subappalto</SelectItem>
                  <SelectItem value="totem">Totem (visualizzazione)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t("common.phone")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm" /></div>
            <div className="col-span-2"><Label>Mezzo (targa/modello)</Label><Input value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })} className="rounded-sm" /></div>
            <div className="col-span-2"><Label>Attrezzatura (separata da virgola)</Label><Input value={form.equipment_str} onChange={(e) => setForm({ ...form, equipment_str: e.target.value })} className="rounded-sm" /></div>
            <div className="col-span-2">
              <Label>{t("auto.indirizzo_casa_per_ottimizzazione_percorso")}</Label>
              <Input
                value={form.home_address}
                onChange={(e) => setForm({ ...form, home_address: e.target.value })}
                placeholder="es. Via Emilia 100, Modena"
                className="rounded-sm"
                data-testid="form-home-address"
              />
              <div className="mt-1 text-[11px] text-slate-500">{t("auto.punto_di_partenza_usato_dallia_per_calcolare_il_giro_ottimal")}</div>
            </div>
            <div><Label>{t("auto.sconto_per_bef")}</Label><Input type="number" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} className="rounded-sm font-mono" /></div>
            <div><Label>{t("auto.meta_mensile_solo_tecnici")}</Label><Input type="number" step="0.01" value={form.monthly_goal} onChange={(e) => setForm({ ...form, monthly_goal: e.target.value })} placeholder="es. 2500" className="rounded-sm font-mono" data-testid="form-monthly-goal" /></div>
            {form.role === "tecnico" && (
              <>
                <div>
                  <Label>Subappalto associato</Label>
                  <select
                    value={form.subappalto_id || ""}
                    onChange={(e) => setForm({ ...form, subappalto_id: e.target.value })}
                    className="w-full rounded-sm border border-slate-300 bg-white p-2 text-sm"
                    data-testid="form-subappalto-id"
                  >
                    <option value="">— Nessuno (tecnico indipendente) —</option>
                    {users.filter((u) => u.role === "subappalto").map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  <div className="mt-1 text-[11px] text-slate-500">Se associato, il BEF di questo tecnico può essere aggregato nel BEF del subappalto.</div>
                </div>
                {form.subappalto_id && (
                  <div>
                    <Label>Seconda scontistica % <span className="text-slate-400">(applicata al margine della 1a)</span></Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={form.discount_percent_2}
                      onChange={(e) => setForm({ ...form, discount_percent_2: e.target.value })}
                      placeholder="es. 10"
                      className="rounded-sm font-mono"
                      data-testid="form-discount-2"
                    />
                    <div className="mt-1 text-[11px] text-slate-500">
                      Esempio: produzione 1000€ − sconto 25% = 250€ margine. Se 2a scontistica = 10% → BEF subappalto = 250 − 10% = <b>225 €</b>.<br />
                      Se lasci 0 → BEF completo emesso al subappalto (produzione − 1a scontistica − materiale/spese).
                    </div>
                  </div>
                )}
              </>
            )}
            {["tecnico", "admin", "assistente", "magazziniere"].includes(form.role) && (
              <div className="col-span-2 rounded-sm border border-slate-200 bg-emerald-50/50 p-3">
                <div className="mb-2 flex items-center gap-2">
                  <div className="text-xs font-bold uppercase tracking-widest text-emerald-800">Cedolino mensile</div>
                  <span className="rounded-sm border border-emerald-300 bg-white px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700">Solo Admin</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Cedolino base fisso mensile (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.cedolino_base}
                      onChange={(e) => setForm({ ...form, cedolino_base: e.target.value })}
                      placeholder="es. 1800"
                      className="rounded-sm font-mono"
                      data-testid="form-cedolino-base"
                    />
                  </div>
                  <div>
                    <Label>Metodo incentivazione</Label>
                    <select
                      value={form.bonus_method}
                      onChange={(e) => setForm({ ...form, bonus_method: e.target.value })}
                      className="mt-2 h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                      data-testid="form-bonus-method"
                    >
                      <option value="nessuno">Nessuno (solo base fisso)</option>
                      <option value="scaglioni">Scaglioni (base + bonus)</option>
                      <option value="percentuale">Percentuale (% produzione)</option>
                    </select>
                  </div>
                </div>
                {form.bonus_method === "scaglioni" && (
                  <div className="mt-3 grid grid-cols-3 gap-3 rounded-sm border border-emerald-200 bg-white p-3">
                    <div>
                      <Label>Soglia (€)</Label>
                      <Input
                        type="number" step="1" min="0"
                        value={form.cedolino_soglia}
                        onChange={(e) => setForm({ ...form, cedolino_soglia: e.target.value })}
                        placeholder="es. 8000"
                        className="rounded-sm font-mono"
                        data-testid="form-cedolino-soglia"
                      />
                    </div>
                    <div>
                      <Label>Step Produzione (€)</Label>
                      <Input
                        type="number" step="1" min="1"
                        value={form.cedolino_step_produzione}
                        onChange={(e) => setForm({ ...form, cedolino_step_produzione: e.target.value })}
                        placeholder="es. 1000"
                        className="rounded-sm font-mono"
                        data-testid="form-cedolino-step-prod"
                      />
                    </div>
                    <div>
                      <Label>Bonus per step (€)</Label>
                      <Input
                        type="number" step="1" min="0"
                        value={form.cedolino_step_bonus}
                        onChange={(e) => setForm({ ...form, cedolino_step_bonus: e.target.value })}
                        placeholder="es. 100"
                        className="rounded-sm font-mono"
                        data-testid="form-cedolino-step-bonus"
                      />
                    </div>
                    <div className="col-span-3 text-[11px] text-slate-600">
                      💡 Oltre <b>{form.cedolino_soglia || 8000}€</b> di produzione, per ogni <b>{form.cedolino_step_produzione || 1000}€</b> in più il tecnico riceve <b>+{form.cedolino_step_bonus || 100}€</b> di bonus.<br />
                      Esempio: produzione 10.000€ con soglia 8000€, step 1000/+100 → 2 step × 100 = <b>+200€ bonus</b> → Cedolino = base ({form.cedolino_base || 1800}) + 200 = <b>{Number(form.cedolino_base || 0) + 200}€</b>.
                    </div>
                  </div>
                )}
                {form.bonus_method === "percentuale" && (
                  <div className="mt-3 grid grid-cols-1 gap-3 rounded-sm border border-emerald-200 bg-white p-3">
                    <div>
                      <Label>Percentuale sulla produzione (%)</Label>
                      <Input
                        type="number" step="0.1" min="0" max="100"
                        value={form.cedolino_bonus_percent}
                        onChange={(e) => setForm({ ...form, cedolino_bonus_percent: e.target.value })}
                        placeholder="es. 30"
                        className="rounded-sm font-mono"
                        data-testid="form-cedolino-percent"
                      />
                    </div>
                    <div className="text-[11px] text-slate-600">
                      💡 Il cedolino è pari a <b>produzione × {form.cedolino_bonus_percent || 30}%</b>, <u>senza base fissa</u>.<br />
                      Esempio: produzione 10.000€ → Cedolino = 10.000 × {form.cedolino_bonus_percent || 30}% = <b>{Math.round(10000 * (Number(form.cedolino_bonus_percent || 30)) / 100)}€</b>.
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="col-span-2">
              <label className="flex cursor-pointer items-center gap-2 rounded-sm border border-slate-200 bg-slate-50 p-2 text-sm" data-testid="form-show-lavorazioni-label">
                <input
                  type="checkbox"
                  checked={form.show_lavorazioni !== false}
                  onChange={(e) => setForm({ ...form, show_lavorazioni: e.target.checked })}
                  className="h-4 w-4"
                  data-testid="form-show-lavorazioni"
                />
                <span>Mostra al tecnico la voce di menu <b>{t("auto.lavorazioni")}</b> con le sue commesse e prezzi</span>
              </label>
            </div>

            {form.role === "tecnico" && (
              <>
                <div className="col-span-2">
                  <label className="flex cursor-pointer items-center gap-2 rounded-sm border border-slate-200 bg-slate-50 p-2 text-sm" data-testid="form-disable-chiusura-label">
                    <input
                      type="checkbox"
                      checked={!!form.disable_chiusura}
                      onChange={(e) => setForm({ ...form, disable_chiusura: e.target.checked })}
                      className="h-4 w-4"
                      data-testid="form-disable-chiusura"
                    />
                    <span>Disabilita per questo tecnico la voce di menu <b>Chiusura Impianto</b></span>
                  </label>
                </div>
                <div className="col-span-2">
                  <label className="flex cursor-pointer items-center gap-2 rounded-sm border border-slate-200 bg-slate-50 p-2 text-sm" data-testid="form-disable-camera-label">
                    <input
                      type="checkbox"
                      checked={!!form.disable_camera_scan}
                      onChange={(e) => setForm({ ...form, disable_camera_scan: e.target.checked })}
                      className="h-4 w-4"
                      data-testid="form-disable-camera"
                    />
                    <span>Disabilita per questo tecnico il caricamento seriali tramite <b>fotocamera</b> in Magazzino Viaggiante</span>
                  </label>
                </div>
              </>
            )}

            {/* Configurazione Totem — solo per utenti con ruolo totem */}
            {form.role === "totem" && (
              <div className="col-span-2 rounded-sm border border-indigo-300 bg-indigo-50/60 p-4" data-testid="totem-config-panel">
                <div className="mb-3 flex items-center gap-2">
                  <LayoutGrid className="h-4 w-4 text-indigo-700" />
                  <div className="text-xs font-bold uppercase tracking-widest text-indigo-800">Configurazione Totem</div>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <Label>Tecnici da mostrare nel Totem</Label>
                    <div className="mt-1 max-h-56 overflow-auto rounded-sm border border-slate-200 bg-white p-2">
                      {users.filter((u) => u.role === "tecnico").length === 0 ? (
                        <div className="p-2 text-xs text-slate-500">Nessun tecnico disponibile</div>
                      ) : (
                        users
                          .filter((u) => u.role === "tecnico")
                          .map((u) => {
                            const checked = (form.totem_tech_ids || []).includes(u.id);
                            return (
                              <label key={u.id} className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1 text-sm hover:bg-slate-50" data-testid={`totem-tech-opt-${u.id}`}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    const cur = new Set(form.totem_tech_ids || []);
                                    if (e.target.checked) cur.add(u.id); else cur.delete(u.id);
                                    setForm({ ...form, totem_tech_ids: Array.from(cur) });
                                  }}
                                  className="h-4 w-4"
                                />
                                <span className="flex-1">{u.name}</span>
                                {u.monthly_goal ? <span className="font-mono text-xs text-slate-500">€ {Number(u.monthly_goal).toLocaleString("it-IT")}</span> : null}
                              </label>
                            );
                          })
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                      <button type="button" className="rounded-sm border border-slate-300 bg-white px-2 py-1 font-semibold hover:bg-slate-50" onClick={() => setForm({ ...form, totem_tech_ids: users.filter((u) => u.role === "tecnico").map((u) => u.id) })} data-testid="totem-tech-select-all">
                        Seleziona tutti
                      </button>
                      <button type="button" className="rounded-sm border border-slate-300 bg-white px-2 py-1 font-semibold hover:bg-slate-50" onClick={() => setForm({ ...form, totem_tech_ids: [] })} data-testid="totem-tech-select-none">
                        Deseleziona
                      </button>
                      <span className="ml-auto">
                        {(form.totem_tech_ids || []).length} selezionat{(form.totem_tech_ids || []).length === 1 ? "o" : "i"}
                      </span>
                    </div>
                    <div className="mt-2 text-[11px] text-slate-500">
                      Se nessun tecnico è selezionato, il Totem mostra tutti i tecnici (come oggi).
                    </div>
                  </div>
                  <div>
                    <Label>Obiettivo di gruppo (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.totem_group_target}
                      onChange={(e) => setForm({ ...form, totem_group_target: e.target.value })}
                      placeholder="es. 20000"
                      className="rounded-sm font-mono"
                      data-testid="form-totem-group-target"
                    />
                    <div className="mt-1 text-[11px] text-slate-500">
                      Appare una slide dedicata nel Totem con la somma dei prodotti dei tecnici selezionati, l'obiettivo e quanto manca.
                      Lascia a <b>0</b> per nasconderla.
                    </div>
                  </div>
                  <TotemMusicPanel />
                </div>
              </div>
            )}

            {/* Materiale ricorrente mensile — solo per tecnici/subappalto già salvati */}
            {editingId && (form.role === "tecnico" || form.role === "subappalto") && (
              <div className="col-span-2 rounded-sm border border-emerald-200 bg-emerald-50/50 p-3" data-testid="recurring-supplies-panel">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-800">
                    <RefreshCw className="h-4 w-4 text-emerald-700" />
                    Materiale ricorrente mensile
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={forceGenerate}
                    className="h-7 rounded-sm text-xs"
                    data-testid="recurring-force-generate"
                  >
                    <RefreshCw className="mr-1 h-3 w-3" /> Rigenera ora
                  </Button>
                </div>
                <p className="mb-3 text-xs text-slate-600">
                  Voci configurate qui vengono <b>automaticamente</b> aggiunte al magazzino viaggiante ogni mese
                  (es. noleggio mezzo, cedolino, canoni). I mesi passati fino a oggi vengono creati subito al salvataggio.
                </p>
                {/* Lista template esistenti */}
                {recurringList.length > 0 && (
                  <div className="mb-3 overflow-auto rounded-sm border border-emerald-100 bg-white">
                    <table className="w-full text-xs">
                      <thead className="bg-emerald-100 text-[10px] uppercase tracking-wider text-emerald-900">
                        <tr>
                          <th className="p-2 text-left">Descrizione</th>
                          <th className="p-2 text-right">Q.tà</th>
                          <th className="p-2 text-right">Prezzo</th>
                          <th className="p-2 text-center">Dal</th>
                          <th className="p-2 text-center">Al</th>
                          <th className="p-2 text-center">Giorno</th>
                          <th className="p-2 text-center">Stato</th>
                          <th className="p-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {recurringList.map((r) => (
                          <tr key={r.id} className="border-t border-emerald-100" data-testid={`recurring-row-${r.id}`}>
                            <td className="p-2 font-medium">{r.material_name}</td>
                            <td className="p-2 text-right font-mono">{r.quantity}</td>
                            <td className="p-2 text-right font-mono">€ {Number(r.unit_price).toFixed(2)}</td>
                            <td className="p-2 text-center font-mono">{r.start_month}</td>
                            <td className="p-2 text-center font-mono">{r.end_month || "—"}</td>
                            <td className="p-2 text-center font-mono">{r.day_of_month}</td>
                            <td className="p-2 text-center">
                              <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-bold ${r.active !== false ? "bg-emerald-600 text-white" : "bg-slate-300 text-slate-700"}`}>
                                {r.active !== false ? "ATTIVO" : "OFF"}
                              </span>
                            </td>
                            <td className="p-2 text-right">
                              <button
                                type="button"
                                onClick={() => editRecurring(r)}
                                className="mr-1 rounded-sm border border-slate-300 bg-white px-1.5 py-0.5 text-slate-700 hover:bg-slate-100"
                                data-testid={`recurring-edit-${r.id}`}
                                title="Modifica"
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteRecurring(r)}
                                className="rounded-sm border border-red-300 bg-white px-1.5 py-0.5 text-red-700 hover:bg-red-50"
                                data-testid={`recurring-delete-${r.id}`}
                                title="Elimina"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {/* Form aggiungi/modifica */}
                <div className="rounded-sm border border-emerald-200 bg-white p-2">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-widest text-emerald-800">
                      {recEditingId ? "Modifica template" : "Aggiungi ricorrente"}
                    </span>
                    {recEditingId && (
                      <button
                        type="button"
                        onClick={() => { setRecEditingId(null); setRecForm(emptyRecurring); }}
                        className="text-slate-400 hover:text-slate-700"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
                    <div className="col-span-2 md:col-span-2">
                      <Label className="text-[10px] uppercase">Materiale *</Label>
                      <Select
                        value={recForm.material_name}
                        onValueChange={(v) => {
                          const m = materials.find((x) => x.name === v);
                          setRecForm({
                            ...recForm,
                            material_name: v,
                            material_id: m?.id || null,
                            // Auto-compila prezzo dal listino materiali (solo se non ancora modificato)
                            unit_price: (m && Number(m.unit_price) > 0 && (!recForm.unit_price || Number(recForm.unit_price) === 0))
                              ? Number(m.unit_price)
                              : recForm.unit_price,
                          });
                        }}
                      >
                        <SelectTrigger className="h-8 rounded-sm text-sm" data-testid="recurring-material-name">
                          <SelectValue placeholder="Seleziona materiale dal catalogo…" />
                        </SelectTrigger>
                        <SelectContent>
                          {materials.length === 0 && (
                            <div className="p-2 text-xs text-slate-400">Nessun materiale nel catalogo</div>
                          )}
                          {materials.map((m) => (
                            <SelectItem key={m.id} value={m.name}>
                              {m.name}
                              {Number(m.unit_price) > 0 && (
                                <span className="ml-2 text-slate-500">— € {Number(m.unit_price).toFixed(2)}</span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="mt-0.5 text-[10px] text-slate-500">
                        Voci non a catalogo (es. Noleggio mezzo, Cedolino) vanno prima aggiunte in <b>Materiali</b>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase">Q.tà</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={recForm.quantity}
                        onChange={(e) => setRecForm({ ...recForm, quantity: e.target.value })}
                        className="h-8 rounded-sm font-mono text-sm"
                        data-testid="recurring-qty"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase">Prezzo €</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={recForm.unit_price}
                        onChange={(e) => setRecForm({ ...recForm, unit_price: e.target.value })}
                        className="h-8 rounded-sm font-mono text-sm"
                        data-testid="recurring-price"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase">Giorno</Label>
                      <Input
                        type="number"
                        min="1"
                        max="28"
                        value={recForm.day_of_month}
                        onChange={(e) => setRecForm({ ...recForm, day_of_month: e.target.value })}
                        className="h-8 rounded-sm font-mono text-sm"
                        data-testid="recurring-day"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase">Attivo</Label>
                      <label className="flex h-8 items-center gap-1.5 rounded-sm border border-slate-200 bg-white px-2 text-xs">
                        <input
                          type="checkbox"
                          checked={recForm.active !== false}
                          onChange={(e) => setRecForm({ ...recForm, active: e.target.checked })}
                          className="h-3.5 w-3.5"
                          data-testid="recurring-active"
                        />
                        <span>{recForm.active !== false ? "ON" : "OFF"}</span>
                      </label>
                    </div>
                    <div className="col-span-2 md:col-span-2">
                      <Label className="text-[10px] uppercase">Dal (mese) *</Label>
                      <Input
                        type="month"
                        value={recForm.start_month}
                        onChange={(e) => setRecForm({ ...recForm, start_month: e.target.value })}
                        className="h-8 rounded-sm font-mono text-sm"
                        data-testid="recurring-start"
                      />
                    </div>
                    <div className="col-span-2 md:col-span-2">
                      <Label className="text-[10px] uppercase">Al (mese) — opzionale</Label>
                      <Input
                        type="month"
                        value={recForm.end_month || ""}
                        onChange={(e) => setRecForm({ ...recForm, end_month: e.target.value })}
                        placeholder="lascia vuoto = a tempo indeterminato"
                        className="h-8 rounded-sm font-mono text-sm"
                        data-testid="recurring-end"
                      />
                    </div>
                    <div className="col-span-2 flex items-end justify-end md:col-span-2">
                      <Button
                        type="button"
                        onClick={submitRecurring}
                        disabled={recBusy || !recForm.material_name.trim()}
                        className="h-8 rounded-sm bg-emerald-600 text-white hover:bg-emerald-700"
                        data-testid="recurring-submit"
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        {recEditingId ? "Aggiorna" : "Aggiungi"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Pannello RBAC granulare: solo l'admin può gestirlo, e solo per utenti non-admin */}
            {form.role && form.role !== "admin" && (
              <div className="col-span-2 rounded-sm border border-blue-200 bg-blue-50/40 p-3" data-testid="rbac-panel">
                <div className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-800">
                  <LayoutGrid className="h-4 w-4 text-[#0052FF]" />
                  Sezioni visibili nel menu
                </div>
                <p className="mb-3 text-xs text-slate-500">
                  Deseleziona le sezioni che questo utente <b>non</b> deve vedere nel menu laterale. L'admin è sempre esente.
                </p>
                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {pagesForRole(form.role).map((p) => {
                    const isHidden = (form.hidden_pages || []).includes(p.path);
                    return (
                      <label key={p.path} className="flex cursor-pointer items-center gap-2 rounded-sm bg-white px-2 py-1.5 text-xs">
                        <input
                          type="checkbox"
                          checked={!isHidden}
                          onChange={(e) => {
                            const cur = new Set(form.hidden_pages || []);
                            if (e.target.checked) cur.delete(p.path); else cur.add(p.path);
                            setForm({ ...form, hidden_pages: Array.from(cur) });
                          }}
                          className="h-3.5 w-3.5 accent-emerald-600"
                          data-testid={`hidden-page-${p.path.replace('/', '')}`}
                        />
                        <span className={isHidden ? "text-slate-400 line-through" : "text-slate-800"}>{p.label}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="mt-3 flex gap-2">
                  <button type="button" className="rounded-sm border border-slate-300 bg-white px-2 py-1 text-xs font-semibold hover:bg-slate-50" onClick={() => setForm({ ...form, hidden_pages: [] })} data-testid="rbac-select-all">
                    ✓ Mostra tutte
                  </button>
                  <button type="button" className="rounded-sm border border-slate-300 bg-white px-2 py-1 text-xs font-semibold hover:bg-slate-50" onClick={() => setForm({ ...form, hidden_pages: pagesForRole(form.role).map((p) => p.path) })} data-testid="rbac-select-none">
                    ✗ Nascondi tutte
                  </button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={save} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="form-save">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openImport} onOpenChange={setOpenImport}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("auto.import_tecnici_da_excel")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-sm bg-slate-50 p-3 text-xs text-slate-600">
              Colonne richieste: <b>{t("auto.email")}</b>, <b>name</b>, <b>password</b>. Opzionali: <b>phone</b>, <b>vehicle</b>, <b>equipment</b> (virgole), <b>discount_percent</b>.
            </div>
            <div>
              <Label>File Excel/CSV</Label>
              <Input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setImpFile(e.target.files?.[0])} className="rounded-sm" data-testid="import-tecnici-file" />
              <button type="button" onClick={downloadTemplate} className="mt-2 text-xs font-medium text-[#0052FF] hover:underline">
                ↓ Scarica template pronto con esempi
              </button>
            </div>
          </div>
          <DialogFooter><Button onClick={doImport} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="import-tecnici-confirm">{t("auto.importa")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
